import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // Synchronously check localStorage on load
    const [user, setUser] = useState(() => {
        try {
            const savedUser = localStorage.getItem("auth_user");
            return savedUser ? JSON.parse(savedUser) : false;
        } catch {
            return false;
        }
    });

    const [error, setError] = useState("");

    // Setup global 401 Interceptor: If any API request fails with 401 (unauthorized), log the user out
    useEffect(() => {
        const interceptor = api.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response && error.response.status === 401) {
                    logout();
                }
                return Promise.reject(error);
            }
        );

        return () => {
            api.interceptors.response.eject(interceptor);
        };
    }, []);

    const login = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/login", { email, password }); 
            // Save user to state AND localStorage
            // CHECK VALIDITY DATE ON FRONTEND
        if (data.user?.validity) {
            const currentDate = new Date();
            const validityDate = new Date(data.user.validity);

            if (currentDate > validityDate) {
                const msg = "Account subscription has expired. Please renew your access.";
                setError(msg);
                return { ok: false, error: msg };
            }
        }
    
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.user));
            setUser(data.user);
           // localStorage.setItem("auth_user", JSON.stringify(data));
            
            return { ok: true };
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            return { ok: false, error: msg };
        }
    };

    const register = async (email, password, name) => {
        setError("");
        try {
            const { data } = await api.post("/auth/register", { email, password, name });
            
            // Save user to state AND localStorage
            setUser(data);
            localStorage.setItem("auth_user", JSON.stringify(data));
            
            return { ok: true };
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            return { ok: false, error: msg };
        }
    };

    const logout = async () => {
        try {
            await api.post("/logout");
        } catch (_) {}
        
        // Clear state AND localStorage
        localStorage.removeItem("auth_user");
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, error, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}